import { SERVICE_AREA_DESCRIPTION } from '../../shared/region';

// Error codes raised by the Postgres API and the Worker, mapped to plain language.

const MESSAGES: Record<string, string> = {
  not_authenticated: 'Your session has ended. Sign in again to continue.',
  not_verified: 'Verify your home location to see and post on your neighborhood board.',
  profile_incomplete: 'Add your name before posting.',
  account_banned: 'This account has been banned from Roadrunner.',
  account_suspended: "Your account is suspended, so you can read but can't post right now.",
  forbidden: "You don't have permission to do that.",
  not_found: "That note isn't available. It may have been deleted, or it's outside your area.",
  body_empty: 'Write something first.',
  body_too_long: "That's a bit long. Trim it down and try again.",
  name_too_short: 'Names need at least 2 characters.',
  name_too_long: 'Names can be up to 40 characters.',
  bio_too_long: 'Keep your bio to 280 characters.',
  rate_limited: "You're posting faster than neighbors usually do. Wait a little and try again.",
  cannot_thank_own: "You can't thank your own note.",
  cannot_block_self: "You can't block yourself.",
  cannot_report_self: "You can't report yourself.",
  already_reported: "You've already reported this. A moderator will look at it.",
  details_too_long: 'Keep report details to 1,000 characters.',
  invalid_target: 'Something went wrong with that request.',
  cannot_moderate_self: "You can't moderate your own account.",
  invalid_duration: 'Suspensions last between 1 and 365 days.',
  reason_required: 'Add a short reason. It goes in the moderation log.',
  // Location verification (Worker)
  bad_request: 'Something was wrong with that request. Reload the page and try again.',
  captcha_failed: "We couldn't confirm you're a person. Try the check again.",
  low_accuracy:
    "Your device gave only a rough location, and trying again right away usually gives the same result. If you're on a computer, switch to your phone. On a phone, turn on Location Services (and Precise Location on iPhone) first, then try again near a window or outside. You get a few tries a day.",
  outside_area: `Roadrunner isn't in your area yet. For now it's open to ${SERVICE_AREA_DESCRIPTION}. If you live here and you're on a computer, try your phone: computers can report the wrong place, especially on satellite internet.`,
  change_cooldown: 'You can change your home area once every 30 days.',
  too_many_attempts: "You've tried to verify several times today. Try again tomorrow.",
  server_error: 'Roadrunner had a problem on our end. Try again in a minute.',
  network: "Couldn't reach Roadrunner. Check your connection and try again.",
  // Photos
  too_many_photos: 'A note can have up to 4 photos.',
  photo_not_found: 'One of those photos expired before it was posted. Remove it and add it again.',
  photo_too_large: 'That photo is too large. Try a different one.',
  photo_invalid: "That file couldn't be read as a photo. Try a JPEG or PNG.",
};

export class ApiError extends Error {
  readonly code: string;

  constructor(code: string, detail?: string) {
    super(detail ?? code);
    this.code = code;
    this.name = 'ApiError';
  }
}

export function isKnownCode(code: string): boolean {
  return code in MESSAGES;
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError && MESSAGES[error.code]) return MESSAGES[error.code]!;
  return 'Something went wrong. Try again.';
}

export function errorCode(error: unknown): string | null {
  return error instanceof ApiError ? error.code : null;
}
